import * as React from 'react';
import { Link, useParams, useNavigate } from 'react-router';
import {
  TrophyIcon,
  MapPinIcon,
  MapIcon,
  ImageIcon,
  CheckCircle2Icon,
  CircleIcon,
  NavigationIcon,
  UsersIcon,
  SparklesIcon,
  ArrowRightIcon,
  LogOutIcon,
  ClockIcon,
  TargetIcon,
  ZapIcon,
  SmartphoneIcon,
  BoxesIcon,
  ScanLineIcon
} from 'lucide-react';

import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  useGetHunt,
  useJoinHunt,
  getGetMyHuntProgressQueryOptions,
  getGetHuntLeaderboardQueryOptions,
  ApiError
} from '@cdf/api-client-react';
import type {
  HuntState,
  HuntProgress,
  HuntLeaderboard,
  PublicStation,
  PublicChallenge,
  LeaderboardEntry
} from '@cdf/api-client-react';

import { useHuntSession, huntAuthHeaders, consumePendingCheckin } from '@/hooks/use-hunt-session';
import { useHuntChallenges } from '@/hooks/use-hunt-challenges';
import {
  ProgramTimeline,
  BoothDirectory,
  BoothCaptureStats
} from '@/components/hunt/algim-program';
import {
  ScheduleTimelineSlider,
  ScheduleStatusBadge,
} from '@/components/hunt/schedule-timeline-slider';
import {
  SessionQuizCard,
  isSessionQuizChallenge,
} from '@/components/hunt/session-quiz-card';
import {
  CONFERENCE_DAY_START_MINUTES,
  parseScheduleMinutes,
  useTimeBasedHunt,
} from '@/hooks/use-time-based-hunt';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useCvTargetsActive, type ActiveCvTarget } from '@/lib/cv-api';

/** Deep-link the map to the hunt area (GPS station centroid, or Melbourne CBD fallback). */
function huntMapHref(stations: PublicStation[]): string {
  const pts = stations.filter(
    (s) =>
      typeof s.latitude === 'number' &&
      Number.isFinite(s.latitude) &&
      typeof s.longitude === 'number' &&
      Number.isFinite(s.longitude),
  );
  if (pts.length === 0) {
    return '/map?lat=-37.8165&lng=144.9669&zoom=16';
  }
  const lat = pts.reduce((sum, s) => sum + (s.latitude as number), 0) / pts.length;
  const lng = pts.reduce((sum, s) => sum + (s.longitude as number), 0) / pts.length;
  return `/map?lat=${lat.toFixed(6)}&lng=${lng.toFixed(6)}&zoom=15`;
}

function HuntExploreActions({ slug, stations }: { slug: string; stations: PublicStation[] }) {
  const mapHref = huntMapHref(stations);
  return (
    <div className="grid grid-cols-3 gap-3">
      <Button asChild size="lg" variant="outline" className="h-12">
        <Link to={`/events/${slug}/twin`}>
          <BoxesIcon className="mr-1 h-5 w-5" aria-hidden />
          3D Twin
        </Link>
      </Button>
      <Button asChild size="lg" variant="outline" className="h-12">
        <Link to={mapHref}>
          <MapIcon className="mr-1 h-5 w-5" aria-hidden />
          Map
        </Link>
      </Button>
      <Button asChild size="lg" variant="outline" className="h-12">
        <Link to="/ar">
          <SmartphoneIcon className="mr-1 h-5 w-5" aria-hidden />
          AR / CV
        </Link>
      </Button>
    </div>
  );
}

// ── Passport screen ─────────────────────────────────────────────────────────

function ProgressSummary({ progress }: { progress: HuntProgress }) {
  const stationsDone = progress.capturedStationIds.length;
  const stationsTotal = progress.stationCount;
  const challengesDone = progress.completedChallengeIds.length;
  const challengesTotal = progress.challengeCount;
  const stationPct = stationsTotal > 0 ? Math.round((stationsDone / stationsTotal) * 100) : 0;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile label="Points" value={progress.totalPoints.toLocaleString()} icon={<ZapIcon className="h-4 w-4" />} />
      <StatTile label="Rank" value={progress.rank ? `#${progress.rank}` : '—'} icon={<TrophyIcon className="h-4 w-4" />} />
      <StatTile label="Stations" value={`${stationsDone}/${stationsTotal}`} icon={<TargetIcon className="h-4 w-4" />} sub={`${stationPct}%`} />
      <StatTile label="Challenges" value={`${challengesDone}/${challengesTotal}`} icon={<CheckCircle2Icon className="h-4 w-4" />} />
    </div>
  );
}

function StatTile({
  label,
  value,
  icon,
  sub
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  sub?: string;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center gap-1 p-3 text-center">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          {icon}
          <span className="text-xs font-medium">{label}</span>
        </div>
        <span className="text-xl font-bold tabular-nums">{value}</span>
        {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
      </CardContent>
    </Card>
  );
}

// ── Inline quiz component ──────────────────────────────────────────────────

function QuizInline({ challenge }: { challenge: PublicChallenge }) {
  const [selectedAnswer, setSelectedAnswer] = React.useState<number | null>(null);
  const [answeredCorrectly, setAnsweredCorrectly] = React.useState(false);
  const { completeChallenge, isCompleting } = useHuntChallenges();

  const cfg = challenge.config;
  if (!cfg?.question || !cfg.options || cfg.options.length === 0) {
    return <Badge variant="outline">Quiz not configured</Badge>;
  }

  const handleAnswer = (index: number) => {
    if (answeredCorrectly || isCompleting) return;
    setSelectedAnswer(index);
    if (cfg.correctIndex === index) {
      setAnsweredCorrectly(true);
      completeChallenge(challenge.id, { selectedAnswer: index });
    }
  };

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">{cfg.question}</p>
      <div className="space-y-1">
        {cfg.options.map((option, index) => {
          const isSelected = selectedAnswer === index;
          const isCorrect = cfg.correctIndex === index;
          return (
            <button
              key={index}
              type="button"
              onClick={() => handleAnswer(index)}
              disabled={answeredCorrectly || isCompleting}
              className={cn(
                'flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors',
                answeredCorrectly && isCorrect && 'border-green-500 bg-green-500/10',
                isSelected && !isCorrect && 'border-red-500 bg-red-500/10',
                !isSelected && !answeredCorrectly && 'hover:bg-muted',
                answeredCorrectly && !isCorrect && 'opacity-50'
              )}
            >
              {option}
              {answeredCorrectly && isCorrect && (
                <CheckCircle2Icon className="h-3.5 w-3.5 text-green-500" />
              )}
              {isSelected && !isCorrect && (
                <CircleIcon className="h-3.5 w-3.5 text-red-500" />
              )}
            </button>
          );
        })}
      </div>
      {selectedAnswer !== null && !answeredCorrectly && (
        <p className="text-xs text-muted-foreground">Not quite — try again!</p>
      )}
      {answeredCorrectly && (
        <p className="text-xs font-medium text-green-600">Correct! +{challenge.points} pts</p>
      )}
    </div>
  );
}

// ── Station card ────────────────────────────────────────────────────────────

function StationCard({
  station,
  captured,
  challenges,
  completedChallengeIds,
  slug
}: {
  station: PublicStation;
  captured: boolean;
  challenges: PublicChallenge[];
  completedChallengeIds: string[];
  slug: string;
}) {
  const isGps = station.kind === 'gps';
  const stationChallenges = challenges.filter((c) => c.stationId === station.id);
  const { data: cvTargetsData } = useCvTargetsActive(slug);
  const cvTargetByChallengeId = React.useMemo(
    () => new Map((cvTargetsData?.targets ?? []).map((target) => [target.huntChallengeId, target])),
    [cvTargetsData?.targets],
  );
  const cvTargets = stationChallenges
    .filter((challenge) => String(challenge.type) === 'cv_scan')
    .map((challenge) => cvTargetByChallengeId.get(challenge.id))
    .filter((target): target is ActiveCvTarget => target !== undefined);
  const hasCvTarget = cvTargets.length > 0;

  return (
    <Card className={cn(captured && 'border-primary/40 bg-primary/5')}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            {isGps ? (
              <NavigationIcon className="h-5 w-5 text-blue-500" />
            ) : (
              <ImageIcon className="h-5 w-5 text-primary" />
            )}
            <CardTitle className="text-base">{station.name}</CardTitle>
          </div>
          {captured ? (
            <Badge variant="default" className="gap-1">
              <CheckCircle2Icon className="h-3 w-3" />
              Captured
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <CircleIcon className="h-3 w-3" />
              {station.points} pts
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        <p className="text-sm text-muted-foreground">
          {captured
            ? 'Nice work! You captured this station.'
            : isGps
              ? 'Head to this location and capture it from the AR/GPS view.'
              : hasCvTarget
                ? 'Find the target image shown below, then scan the real image with the AR scanner.'
                : 'Find the image at this station and scan it to capture.'}
        </p>
        {isGps && !captured && station.latitude != null && station.longitude != null && (
          <Button asChild size="sm" variant="outline">
            <Link to={`/hunt/${slug}/capture/${station.id}`}>
              <NavigationIcon className="mr-1 h-4 w-4" />
              Open GPS Capture
            </Link>
          </Button>
        )}
        {stationChallenges.length > 0 && (
          <div className="space-y-1.5 pt-1">
            {stationChallenges.map((ch) => {
              const done = completedChallengeIds.includes(ch.id);
              const isQuiz = ch.type === 'quiz';
              const isCvScan = String(ch.type) === 'cv_scan';
              if (isSessionQuizChallenge(ch)) {
                return (
                  <SessionQuizCard
                    key={ch.id}
                    challenge={ch}
                    completed={done}
                    huntSlug={slug}
                    compact
                  />
                );
              }
              if (!done && isQuiz) {
                return (
                  <div key={ch.id} className="rounded-md border border-border p-2.5">
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-sm font-medium">
                        <CircleIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        {ch.title}
                      </span>
                      <span className="text-xs text-muted-foreground">{ch.points} pts</span>
                    </div>
                    <QuizInline challenge={ch} />
                  </div>
                );
              }
              if (!done && isCvScan) {
                return (
                  <div key={ch.id} className="rounded-md border border-violet-500/30 bg-violet-500/5 p-2.5">
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-sm font-medium">
                        <ScanLineIcon className="h-3.5 w-3.5 text-violet-500" />
                        {ch.title}
                      </span>
                      <span className="text-xs text-muted-foreground">{ch.points} pts</span>
                    </div>
                    {cvTargetByChallengeId.get(ch.id) && (
                      <TargetImage
                        imageUrl={cvTargetByChallengeId.get(ch.id)?.imageUrl}
                        title={ch.title}
                      />
                    )}
                    {ch.instructions && (
                      <p className="mb-2 text-xs text-muted-foreground">{ch.instructions}</p>
                    )}
                    <Button asChild size="sm" variant="outline">
                      <Link to="/ar">
                        <ScanLineIcon className="mr-1 h-3.5 w-3.5" />
                        Open AR Scanner
                      </Link>
                    </Button>
                  </div>
                );
              }
              return (
                <div
                  key={ch.id}
                  className={cn(
                    'flex items-center justify-between rounded-md border px-3 py-1.5 text-sm',
                    done ? 'border-primary/30 bg-primary/5' : 'border-border'
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    {done ? (
                      <CheckCircle2Icon className="h-3.5 w-3.5 text-primary" />
                    ) : (
                      <CircleIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    {ch.title}
                  </span>
                  <span className="text-xs text-muted-foreground">{ch.points} pts</span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Leaderboard section ─────────────────────────────────────────────────────

function LeaderboardSection({
  leaderboard,
  myParticipantId
}: {
  leaderboard: HuntLeaderboard;
  myParticipantId: string;
}) {
  const top = leaderboard.individuals.slice(0, 10);
  const myEntry = leaderboard.individuals.find((e) => e.participantId === myParticipantId);
  const showMine = myEntry && !top.some((e) => e.participantId === myParticipantId);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <TrophyIcon className="h-4 w-4 text-amber-500" />
          Leaderboard
        </CardTitle>
        <CardDescription>{leaderboard.participantCount} participants</CardDescription>
      </CardHeader>
      <CardContent className="space-y-1">
        {top.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No captures yet — be the first!
          </p>
        ) : (
          top.map((entry) => <LeaderboardRow key={entry.rank} entry={entry} isMe={entry.participantId === myParticipantId} />)
        )}
        {showMine && myEntry && (
          <>
            <div className="my-1 border-t border-dashed" />
            <LeaderboardRow entry={myEntry} isMe />
          </>
        )}
        {leaderboard.teams.length > 0 && (
          <>
            <div className="my-2 border-t" />
            <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
              <UsersIcon className="h-3 w-3" />
              TEAMS
            </p>
            {leaderboard.teams.slice(0, 5).map((team) => (
              <div key={team.rank} className="flex items-center justify-between py-0.5 text-sm">
                <span className="flex items-center gap-2">
                  <span className="w-5 text-right text-xs text-muted-foreground">{team.rank}.</span>
                  {team.name}
                </span>
                <span className="font-medium tabular-nums">{team.totalPoints.toLocaleString()}</span>
              </div>
            ))}
          </>
        )}
        {leaderboard.recentCaptures.length > 0 && (
          <>
            <div className="my-2 border-t" />
            <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
              <ClockIcon className="h-3 w-3" />
              RECENT CAPTURES
            </p>
            {leaderboard.recentCaptures.slice(0, 5).map((cap, i) => (
              <div key={i} className="truncate py-0.5 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{cap.nickname}</span>
                {cap.team && <span className="text-muted-foreground"> ({cap.team})</span>}
                {' '}captured {cap.stationName}
              </div>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function LeaderboardRow({ entry, isMe }: { entry: LeaderboardEntry; isMe: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-md px-2 py-1.5 text-sm',
        isMe && 'bg-primary/10 font-medium'
      )}
    >
      <span className="flex items-center gap-2">
        <span className="w-5 text-right text-xs text-muted-foreground">
          {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `${entry.rank}.`}
        </span>
        {entry.name}
      </span>
      <span className="tabular-nums">{entry.totalPoints.toLocaleString()}</span>
    </div>
  );
}

// ── Passport screen ─────────────────────────────────────────────────────────

function Passport({
  hunt,
  slug,
  session,
  onLeave
}: {
  hunt: HuntState;
  slug: string;
  session: { participantId: string; token: string; nickname: string; team: string | null };
  onLeave: () => void;
}) {
  const authReq = huntAuthHeaders(session.token);
  const [timelineMinutes, setTimelineMinutes] = React.useState(CONFERENCE_DAY_START_MINUTES);
  const schedule = useTimeBasedHunt({
    stations: hunt.stations,
    challenges: hunt.challenges,
    selectedTime: timelineMinutes,
  });

  const progressQuery = useQuery({
    ...getGetMyHuntProgressQueryOptions(slug, { request: authReq }),
    refetchInterval: 10_000
  });

  const leaderboardQuery = useQuery({
    ...getGetHuntLeaderboardQueryOptions(slug, { limit: 20 }),
    refetchInterval: 8_000
  });

  React.useEffect(() => {
    if (progressQuery.error instanceof ApiError && progressQuery.error.status === 401) {
      onLeave();
    }
  }, [onLeave, progressQuery.error]);

  const completedChallengeIds = progressQuery.data?.completedChallengeIds ?? [];

  const completedSessionMarkers = React.useMemo(() => {
    const markers: number[] = [];
    for (const ch of hunt.challenges) {
      if (!completedChallengeIds.includes(ch.id) || !isSessionQuizChallenge(ch)) continue;
      const start = parseScheduleMinutes(ch.startTime);
      const end = parseScheduleMinutes(ch.endTime);
      if (start != null) markers.push(start);
      if (end != null) markers.push(end);
    }
    return markers;
  }, [completedChallengeIds, hunt.challenges]);

  const completedSessionRooms = React.useMemo(() => {
    const rooms = new Set<string>();
    for (const ch of hunt.challenges) {
      if (!completedChallengeIds.includes(ch.id) || !isSessionQuizChallenge(ch)) continue;
      if (ch.room?.trim()) rooms.add(ch.room.trim());
    }
    return Array.from(rooms).sort((a, b) => a.localeCompare(b));
  }, [completedChallengeIds, hunt.challenges]);

  const isClosed = hunt.status === 'closed';
  const orderedStations = React.useMemo(() => {
    const discoveryTargets: PublicStation[] = [];
    const remainingStations: PublicStation[] = [];

    for (const station of hunt.stations) {
      if (station.name.startsWith('ALGIM Discovery Wall — Target ')) {
        discoveryTargets.push(station);
      } else {
        remainingStations.push(station);
      }
    }

    return [...discoveryTargets, ...remainingStations];
  }, [hunt.stations]);

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">{hunt.name}</h1>
          <p className="text-sm text-muted-foreground">
            {session.nickname}
            {session.team && ` · Team ${session.team}`}
            {isClosed && ' · Hunt closed'}
          </p>
        </div>
        {!isClosed && (
          <Button variant="ghost" size="sm" onClick={onLeave} className="text-muted-foreground">
            <LogOutIcon className="mr-1 h-4 w-4" />
            Leave
          </Button>
        )}
      </div>

      <HuntExploreActions slug={slug} stations={hunt.stations} />

      {/* Progress */}
      {progressQuery.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : progressQuery.isError ? (
        <Card>
          <CardContent className="p-4 text-center text-sm text-destructive">
            Could not load your progress. {progressQuery.error instanceof ApiError && progressQuery.error.status === 401
              ? 'Your session may have expired — try leaving and rejoining.'
              : 'Please try again.'}
          </CardContent>
        </Card>
      ) : progressQuery.data ? (
        <ProgressSummary progress={progressQuery.data} />
      ) : null}

      {/* Completion bonus badge */}
      {progressQuery.data?.completionBonusAwarded && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm">
          <SparklesIcon className="h-4 w-4 text-amber-500" />
          <span className="font-medium">Full hunt complete — bonus points awarded!</span>
        </div>
      )}

      {/* Stations */}
      <div>
        <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold">
          <MapPinIcon className="h-5 w-5" />
          Stations
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Card className="border-primary/20 bg-primary/[0.03] sm:col-span-2">
            <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
              <div>
                <p className="font-medium">See something worth sharing?</p>
                <p className="text-sm text-muted-foreground">Attach a photo with your GPS location to pin it in this event's digital twin.</p>
              </div>
              <Button asChild>
                <Link to={`/report?purpose=event&event=${encodeURIComponent(slug)}&eventName=${encodeURIComponent(hunt.name)}`}>
                  Report for this event
                </Link>
              </Button>
            </CardContent>
          </Card>
          {orderedStations.map((station) => (
            <StationCard
              key={station.id}
              station={station}
              captured={progressQuery.data?.capturedStationIds.includes(station.id) ?? false}
              challenges={hunt.challenges}
              completedChallengeIds={progressQuery.data?.completedChallengeIds ?? []}
              slug={slug}
            />
          ))}
        </div>
      </div>

      {/* Booth Capture Stats */}
      {progressQuery.data && (
        <BoothCaptureStats
          stations={hunt.stations}
          capturedStationIds={progressQuery.data.capturedStationIds}
        />
      )}

      {/* Interactive conference timeline */}
      <ScheduleTimelineSlider
        value={timelineMinutes}
        onChange={setTimelineMinutes}
        markers={schedule.sessionMarkers}
        completedMarkers={completedSessionMarkers}
        counts={schedule.counts}
        liveRoomNames={schedule.liveRooms.map((room) => room.room)}
        completedRoomNames={completedSessionRooms}
      />

      {schedule.liveRooms.length > 0 && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Live sessions & quizzes · {schedule.selectedTime}</CardTitle>
            <CardDescription>
              Answer attendance quizzes while the session is live on the event clock
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {schedule.liveRooms.map((room) => {
              const sessionQuizzes = room.challenges.filter((c) => isSessionQuizChallenge(c));
              return (
                <div
                  key={room.room}
                  className="space-y-2 rounded-lg border border-emerald-500/25 bg-background/80 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <p className="text-sm font-medium">{room.room}</p>
                      <p className="text-xs text-muted-foreground">
                        {[
                          ...room.stations.map((s) => s.name),
                          ...room.challenges.map((c) => c.title),
                        ]
                          .slice(0, 3)
                          .join(' · ')}
                        {room.liveCount > 3 ? ` · +${room.liveCount - 3} more` : ''}
                      </p>
                    </div>
                    <ScheduleStatusBadge status="live" count={room.liveCount} />
                  </div>
                  {sessionQuizzes.length > 0 ? (
                    sessionQuizzes.map((challenge) => (
                      <SessionQuizCard
                        key={challenge.id}
                        challenge={challenge}
                        completed={completedChallengeIds.includes(challenge.id)}
                        selectedMinutes={timelineMinutes}
                        scheduleStatus={challenge.status}
                        huntSlug={slug}
                        compact
                      />
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      No attendance quiz for this room — visit the station QR for other challenges.
                    </p>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Static program overview */}
      <ProgramTimeline />

      {/* Booth Directory */}
      {progressQuery.data && (
        <BoothDirectory
          stations={hunt.stations}
          challenges={hunt.challenges}
          capturedStationIds={progressQuery.data.capturedStationIds}
          completedChallengeIds={progressQuery.data.completedChallengeIds}
        />
      )}

      {/* Leaderboard */}
      {leaderboardQuery.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : leaderboardQuery.data ? (
        <LeaderboardSection leaderboard={leaderboardQuery.data} myParticipantId={session.participantId} />
      ) : null}

      {/* Closed hunt — link to full results */}
      {isClosed && (
        <Button asChild className="w-full">
          <Link to={`/hunt/${slug}/leaderboard/live`}>
            <TrophyIcon className="mr-2 h-4 w-4" />
            View Final Results
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      )}
    </div>
  );
}

// ── Not-open message ────────────────────────────────────────────────────────

function HuntNotOpen({ message }: { message: string }) {
  return (
    <Card className="mx-auto w-full max-w-md">
      <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
          <ClockIcon className="h-7 w-7 text-muted-foreground" />
        </div>
        <p className="text-lg font-medium">{message}</p>
        <Button asChild variant="outline">
          <Link to="/">Back to Home</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function TargetImage({ imageUrl, title }: { imageUrl: string | undefined; title: string }) {
  const [open, setOpen] = React.useState(false);
  if (!imageUrl) return null;
  return (
    <figure className="mb-2 overflow-hidden rounded-md border border-violet-500/20 bg-background">
      <img
        src={imageUrl}
        alt={`Target image for ${title}`}
        className="aspect-[4/3] w-full cursor-zoom-in object-cover transition-opacity hover:opacity-90"
        loading="lazy"
        onClick={() => setOpen(true)}
      />
      <figcaption className="px-2 py-1.5 text-xs text-muted-foreground">
        Find this image in the venue, then scan it.
      </figcaption>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0">
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <img
            src={imageUrl}
            alt={`Target image for ${title}`}
            className="w-full object-contain"
          />
        </DialogContent>
      </Dialog>
    </figure>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

export function HuntPage() {
  const params = useParams();
  const requestedSlug = params.huntSlug ?? '';
  const slug = requestedSlug === 'novotel-collins-conference'
    ? 'algim-2026-exchange'
    : requestedSlug;

  const { session, saveSession, clearSession } = useHuntSession(slug);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (requestedSlug === 'novotel-collins-conference') {
      navigate('/hunt/algim-2026-exchange', { replace: true });
    }
  }, [navigate, requestedSlug]);

  // Join mutation — store the result as a session on success
  const joinMutation = useJoinHunt({
    mutation: {
      onSuccess: (data) => {
        saveSession({
          huntSlug: slug,
          participantId: data.participantId,
          token: data.token,
          nickname: data.nickname,
          team: data.team ?? null
        });
        // If the visitor arrived via a QR check-in deep link, redirect
        // back to the check-in route so the capture completes automatically.
        const pending = consumePendingCheckin();
        if (pending && pending.slug === slug) {
          navigate(`/hunt/${slug}/checkin/${pending.stationCode}`);
        }
      }
    }
  });

  const huntQuery = useGetHunt(slug);

  // Loading
  if (huntQuery.isLoading) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-4 p-4">
        <Skeleton className="h-12 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // Hunt not found or DB unavailable
  if (huntQuery.isError) {
    const status = huntQuery.error instanceof ApiError ? huntQuery.error.status : 0;
    if (status === 404) {
      return <HuntNotOpen message="This hunt doesn't exist or has been removed." />;
    }
    if (status === 503) {
      return <HuntNotOpen message="The treasure hunt service is temporarily unavailable. Please try again shortly." />;
    }
    return <HuntNotOpen message="Something went wrong loading this hunt." />;
  }

  const hunt = huntQuery.data;
  if (!hunt) return null;

  // Draft hunts — not open yet
  if (hunt.status === 'draft') {
    return <HuntNotOpen message="This treasure hunt hasn't opened yet. Check back soon!" />;
  }

  // Has a session (active or closed) — show passport
  if (session) {
    return (
      <div className="min-h-dvh bg-background p-4">
        <Passport
          hunt={hunt}
          slug={slug}
          session={session}
          onLeave={clearSession}
        />
      </div>
    );
  }

  // No session — show join form (join mutation result is stored via onSuccess)
  // Use the join mutation from the top-level so saveSession is wired
  return (
    <div className="min-h-dvh bg-background p-4">
      <JoinFormWithMutation
        hunt={hunt}
        slug={slug}
        isPending={joinMutation.isPending}
        onSubmit={(nickname, team) => {
          joinMutation.mutate({ slug, data: { nickname, team: team || undefined } });
        }}
        error={joinMutation.error}
      />
    </div>
  );
}

// Wrapper that uses the parent joinMutation (avoids duplicate hook in JoinForm)
function JoinFormWithMutation({
  hunt,
  slug,
  isPending,
  onSubmit,
  error
}: {
  hunt: HuntState;
  slug: string;
  isPending: boolean;
  onSubmit: (nickname: string, team: string) => void;
  error: unknown;
}) {
  const [nickname, setNickname] = React.useState('');
  const [team, setTeam] = React.useState('');
  const [localError, setLocalError] = React.useState<string | null>(null);

  const errorMsg = localError ?? (error instanceof ApiError ? error.message : error ? 'Could not join the hunt. Please try again.' : null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = nickname.trim();
    if (!trimmed) {
      setLocalError('Please enter a nickname.');
      return;
    }
    if (trimmed.length > 30) {
      setLocalError('Nickname must be 30 characters or fewer.');
      return;
    }
    setLocalError(null);
    onSubmit(trimmed, team.trim().slice(0, 30));
  };

  return (
    <Card className="mx-auto w-full max-w-md">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <SparklesIcon className="h-7 w-7 text-primary" />
        </div>
        <CardTitle className="text-2xl">{hunt.name}</CardTitle>
        {hunt.description && (
          <CardDescription className="text-base">{hunt.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <HuntExploreActions slug={slug} stations={hunt.stations} />
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nickname">Nickname</Label>
            <Input
              id="nickname"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Your display name"
              maxLength={30}
              autoComplete="off"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="team">Team (optional)</Label>
            <Input
              id="team"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              placeholder="Join or create a team"
              maxLength={30}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              Teams compete on the leaderboard too. Leave blank to play solo.
            </p>
          </div>
          {errorMsg && (
            <p className="text-sm text-destructive" role="alert">{errorMsg}</p>
          )}
          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? 'Joining…' : 'Join the Hunt'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
